/* eslint-disable max-lines */
import { useId, useState } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import ChevronDown from '@icon/ChevronDown';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import type {
  RecommendedDurationBucket,
  RecommendedUpsellPlan
} from '@api/fetchers/goal/test-tab/types';

import type {
  RecommendedAvailableOption,
  RecommendedCardData,
  RecommendedDurationOption,
  RecommendedExhaustedSummary
} from '../testTab/recommended';

const HERO_ART = getImage('test-series-listing/recommended/hero-art.svg');
const DIVIDER_SPARKLE = getImage(
  'test-series-listing/recommended/divider-sparkle.svg'
);
const PLUS_PITCH_GLOW = getImage(
  'test-series-listing/recommended/plus-pitch-glow.svg'
);
const PLUS_PITCH_ICON = getImage(
  'test-series-listing/recommended/plus-pitch-icon.svg'
);
const PRIME_PITCH_ART = getImage('prime-pitch/prime-box.svg');

const BUCKET_ACCENT: Record<RecommendedDurationBucket, string> = {
  short: 'var(--color-green-2)',
  medium: 'var(--color-blue-text)',
  long: 'var(--color-purple-dark)'
};

const getAccent = (bucket: RecommendedDurationBucket) =>
  BUCKET_ACCENT[bucket] ?? 'var(--color-purple-dark)';

const UPSELL_FILL: Record<RecommendedUpsellPlan, string> = {
  prime: 'var(--color-i-prime-pitch-cta)',
  plus: 'var(--color-green-2)'
};

const getUpsellFill = (plan: RecommendedUpsellPlan) =>
  UPSELL_FILL[plan] ?? UPSELL_FILL.plus;

const getOptionCtaAccent = (option: RecommendedDurationOption) =>
  'upsellPlan' in option
    ? getUpsellFill(option.upsellPlan)
    : getAccent(option.durationBucket);

const reveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Section = styled.section<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  grid-column: span 18;
  width: 852px;
  max-width: 100%;
  margin-bottom: ${({ $isCompact }) =>
    $isCompact ? Spacings.SPACING_4B : Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    width: 100%;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const getPitchSurface = (plan?: RecommendedUpsellPlan) => {
  if (plan === 'prime') {
    return 'var(--color-i-prime-surface-dark)';
  }

  if (plan === 'plus') {
    return 'var(--color-i-plus-pitch)';
  }

  return 'var(--color-purple)';
};

const getPitchHeaderGutter = (plan?: RecommendedUpsellPlan) =>
  plan ? '168px' : '150px';

const Card = styled.div<{
  $isCompact?: boolean;
  $plan?: RecommendedUpsellPlan;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $isCompact }) =>
    $isCompact ? 'center' : 'flex-start'};
  gap: ${({ $isCompact }) =>
    $isCompact ? Spacings.SPACING_3B : Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  min-height: ${({ $isCompact }) => ($isCompact ? '56px' : 'unset')};
  border-radius: ${({ $isCompact }) => ($isCompact ? '12px' : '16px')};
  background: ${({ $plan }) => getPitchSurface($plan)};
  overflow: hidden;
  transition: padding 0.32s ease, border-radius 0.32s ease, gap 0.32s ease,
    min-height 0.32s ease;
`;

const CompactCopy = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  min-width: 0;
  height: 32px;

  ${mediaQuery.uptoMobile} {
    height: auto;
    min-height: 32px;
    flex-wrap: wrap;
  }
`;

const CompactIcon = styled.img`
  display: block;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const CompactRule = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 12px;
  background: var(--color-purple-dark);
  opacity: 0.35;
`;

const HeroArt = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  width: 166px;
  height: 100px;
  pointer-events: none;
  mask-image: linear-gradient(to right, transparent 0, black 48px);
`;

const PitchArt = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;
`;

const PlusArtWrap = styled(PitchArt)`
  width: 160px;
  height: 112px;
`;

const PlusGlow = styled.img`
  position: absolute;
  top: -24px;
  right: -16px;
  width: 160px;
  height: 161px;
`;

const PlusIcon = styled.img`
  position: absolute;
  top: 12px;
  right: 28px;
  width: 64px;
  height: 64px;
`;

const PrimeArt = styled.img`
  display: block;
  width: 155px;
  height: 167px;
`;

const Header = styled.div<{ $plan?: RecommendedUpsellPlan }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-right: ${({ $plan }) => getPitchHeaderGutter($plan)};
  z-index: 1;
`;

const Eyebrow = styled.span<{ $plan?: RecommendedUpsellPlan }>`
  font-size: 10px;
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: ${({ $plan }) =>
    $plan === 'plus'
      ? 'var(--color-i-plus-pitch-eyebrow)'
      : 'var(--color-purple-dark)'};
  ${({ $plan }) =>
    $plan === 'prime'
      ? `
    background: linear-gradient(90deg, #774add 43.75%, #e3d3ff 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  `
      : ''}
`;

const Title = styled.h3<{ $plan?: RecommendedUpsellPlan }>`
  margin: 0;
  font-size: 18px;
  line-height: 22px;
  font-weight: ${Fonts.SEMIBOLD};
  color: ${({ $plan }) =>
    // eslint-disable-next-line no-nested-ternary
    $plan === 'prime'
      ? 'var(--color-i-white)'
      : $plan === 'plus'
      ? 'var(--color-i-black-shade-text)'
      : 'var(--color-text-primary)'};
`;

const CompactTitle = styled(Title)`
  white-space: nowrap;

  ${mediaQuery.uptoMobile} {
    white-space: normal;
  }
`;

const CompactSubtitle = styled(Eyebrow)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToggleButton = styled.button<{ $isExpanded: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-green-2);
  border-radius: 4px;
  background: ${({ $isExpanded }) =>
    $isExpanded ? 'transparent' : 'var(--color-green-2)'};
  color: ${({ $isExpanded }) =>
    $isExpanded ? 'var(--color-green-2)' : 'var(--color-i-white)'};
  font-size: 14px;
  line-height: 18px;
  font-weight: ${Fonts.BOLD};
  cursor: pointer;

  svg {
    transition: transform 0.2s ease;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0')});
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const UpsellButton = styled.button<{ $plan: RecommendedUpsellPlan }>`
  position: relative;
  z-index: 1;
  align-self: flex-start;
  height: 32px;
  padding: 0 ${Spacings.SPACING_3B};
  border: 1px solid ${({ $plan }) => getUpsellFill($plan)};
  border-radius: 4px;
  background: ${({ $plan }) => getUpsellFill($plan)};
  color: var(--color-i-white);
  font-size: 14px;
  line-height: 18px;
  font-weight: ${Fonts.BOLD};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const CompactRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacings.SPACING_3B};
  width: 100%;
  min-height: 32px;

  ${ToggleButton}, ${UpsellButton} {
    align-self: center;
    flex-shrink: 0;
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  animation: ${reveal} 0.25s ease-out;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px dashed var(--color-purple-dark);
    opacity: 0.4;
  }
`;

const DividerLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  padding: 2px ${Spacings.SPACING_2B};
  border-radius: 16px;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.BOLD};
  color: var(--color-purple-dark);
`;

const Sparkle = styled.img<{ $isMirrored?: boolean }>`
  width: 13px;
  height: 13px;
  transform: ${({ $isMirrored }) => ($isMirrored ? 'scaleY(-1)' : 'none')};
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${Spacings.SPACING_2B};
`;

const OptionCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B};
  border-radius: 8px;
  background: var(--color-base-1);
`;

const OptionBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

const OptionIcon = styled.img<{ $isExhausted: boolean }>`
  width: 37px;
  height: 37px;
  object-fit: contain;
  opacity: ${({ $isExhausted }) => ($isExhausted ? 0.4 : 1)};
`;

const OptionLabel = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
`;

const OptionDescription = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-secondary);
  white-space: pre-line;
`;

const StartButton = styled.button<{ $accent: string }>`
  height: 24px;
  padding: 0 ${Spacings.SPACING_2B};
  border: 1px solid ${({ $accent }) => $accent};
  border-radius: 4px;
  background: transparent;
  color: ${({ $accent }) => $accent};
  font-size: 14px;
  line-height: 18px;
  font-weight: ${Fonts.SEMIBOLD};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

type RecommendedUpsellMeta = {
  scope: 'exhausted' | 'section';
  durationBucket?: RecommendedDurationBucket;
};

interface RecommendedCardProps {
  data: RecommendedCardData;
  isCompact?: boolean;
  onStart: (option: RecommendedAvailableOption) => void;
  onUpsell: (plan: RecommendedUpsellPlan, meta: RecommendedUpsellMeta) => void;
  onToggle?: (action: 'Opened' | 'Closed') => void;
}

const PitchIllustration = ({ plan }: { plan: RecommendedUpsellPlan }) =>
  plan === 'prime' ? (
    <PitchArt>
      <PrimeArt
        src={PRIME_PITCH_ART}
        alt="Unacademy Prime subscription offer"
      />
    </PitchArt>
  ) : (
    <PlusArtWrap>
      <PlusGlow src={PLUS_PITCH_GLOW} alt="" aria-hidden />
      <PlusIcon src={PLUS_PITCH_ICON} alt="" aria-hidden />
    </PlusArtWrap>
  );

const ExhaustedSummaryCard = ({
  sectionTitle,
  exhausted,
  isCompact,
  onUpsell
}: {
  sectionTitle: string | null;
  exhausted: RecommendedExhaustedSummary;
  isCompact: boolean;
  onUpsell: RecommendedCardProps['onUpsell'];
}) => (
  <Section aria-label={sectionTitle ?? undefined} $isCompact={isCompact}>
    {!isCompact && sectionTitle ? (
      <SectionTitle>{sectionTitle}</SectionTitle>
    ) : null}
    <Card $isCompact={isCompact} $plan={exhausted.plan}>
      {isCompact ? (
        <CompactRow>
          <CompactCopy>
            <CompactIcon
              src={
                exhausted.plan === 'plus' ? PLUS_PITCH_ICON : DIVIDER_SPARKLE
              }
              alt=""
              aria-hidden
            />
            {exhausted.title ? (
              <CompactTitle $plan={exhausted.plan}>
                {exhausted.title}
              </CompactTitle>
            ) : null}
            {exhausted.eyebrow ? (
              <>
                <CompactRule />
                <CompactSubtitle $plan={exhausted.plan}>
                  {exhausted.eyebrow}
                </CompactSubtitle>
              </>
            ) : null}
          </CompactCopy>
          {exhausted.cta ? (
            <UpsellButton
              type="button"
              $plan={exhausted.plan}
              onClick={() => onUpsell(exhausted.plan, { scope: 'exhausted' })}
            >
              {exhausted.cta}
            </UpsellButton>
          ) : null}
        </CompactRow>
      ) : (
        <>
          <PitchIllustration plan={exhausted.plan} />
          <Header $plan={exhausted.plan}>
            {exhausted.eyebrow ? (
              <Eyebrow $plan={exhausted.plan}>{exhausted.eyebrow}</Eyebrow>
            ) : null}
            {exhausted.title ? (
              <Title $plan={exhausted.plan}>{exhausted.title}</Title>
            ) : null}
          </Header>
          {exhausted.cta ? (
            <UpsellButton
              type="button"
              $plan={exhausted.plan}
              onClick={() => onUpsell(exhausted.plan, { scope: 'exhausted' })}
            >
              {exhausted.cta}
            </UpsellButton>
          ) : null}
        </>
      )}
    </Card>
  </Section>
);

const RecommendedCard = ({
  data,
  isCompact = false,
  onStart,
  onUpsell,
  onToggle
}: RecommendedCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const optionsId = useId();

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const next = !prev;
      onToggle?.(next ? 'Opened' : 'Closed');
      return next;
    });
  };

  if (data.summaryState !== 'available' && data.exhausted) {
    return (
      <ExhaustedSummaryCard
        sectionTitle={data.sectionTitle}
        exhausted={data.exhausted}
        isCompact={isCompact}
        onUpsell={onUpsell}
      />
    );
  }

  const handleOptionClick = (option: RecommendedDurationOption) => {
    if ('upsellPlan' in option) {
      onUpsell(option.upsellPlan, {
        scope: 'section',
        durationBucket: option.durationBucket
      });
    } else {
      onStart(option);
    }
  };

  const title = isExpanded
    ? data.titleExpanded ?? data.titleCollapsed
    : data.titleCollapsed ?? data.titleExpanded;
  const toggleLabel = isExpanded
    ? data.ctaExpanded ?? data.ctaCollapsed
    : data.ctaCollapsed ?? data.ctaExpanded;

  return (
    <Section aria-label={data.sectionTitle ?? undefined} $isCompact={isCompact}>
      {!isCompact && data.sectionTitle ? (
        <SectionTitle>{data.sectionTitle}</SectionTitle>
      ) : null}
      <Card $isCompact={isCompact}>
        {isCompact ? (
          <CompactRow>
            <CompactCopy>
              <CompactIcon src={DIVIDER_SPARKLE} alt="" aria-hidden />
              {data.titleCollapsed || title ? (
                <CompactTitle>{data.titleCollapsed || title}</CompactTitle>
              ) : null}
              {data.eyebrow ? (
                <>
                  <CompactRule />
                  <CompactSubtitle>{data.eyebrow}</CompactSubtitle>
                </>
              ) : null}
            </CompactCopy>
            <ToggleButton
              type="button"
              $isExpanded={isExpanded}
              aria-expanded={isExpanded}
              aria-controls={optionsId}
              onClick={handleToggle}
            >
              {toggleLabel}
              <ChevronDown size="16px" color="currentColor" />
            </ToggleButton>
          </CompactRow>
        ) : (
          <>
            <HeroArt src={HERO_ART} alt="" aria-hidden />
            <Header>
              {data.eyebrow ? <Eyebrow>{data.eyebrow}</Eyebrow> : null}
              {title ? <Title>{title}</Title> : null}
            </Header>
            <ToggleButton
              type="button"
              $isExpanded={isExpanded}
              aria-expanded={isExpanded}
              aria-controls={optionsId}
              onClick={handleToggle}
            >
              {toggleLabel}
              <ChevronDown size="16px" color="currentColor" />
            </ToggleButton>
          </>
        )}
        {isExpanded ? (
          <Options id={optionsId}>
            {data.dividerText ? (
              <Divider>
                <DividerLabel>
                  <Sparkle src={DIVIDER_SPARKLE} alt="" aria-hidden />
                  {data.dividerText}
                  <Sparkle
                    src={DIVIDER_SPARKLE}
                    alt=""
                    aria-hidden
                    $isMirrored
                  />
                </DividerLabel>
              </Divider>
            ) : null}
            <OptionGrid>
              {data.options.map((option) => (
                <OptionCard key={option.durationBucket}>
                  <OptionBody>
                    {option.iconUrl ? (
                      <OptionIcon
                        src={option.iconUrl}
                        alt=""
                        $isExhausted={option.isExhausted}
                      />
                    ) : null}
                    <OptionLabel>{option.label}</OptionLabel>
                    {option.description ? (
                      <OptionDescription>
                        {option.description}
                      </OptionDescription>
                    ) : null}
                  </OptionBody>
                  <StartButton
                    type="button"
                    $accent={getOptionCtaAccent(option)}
                    aria-label={
                      option.cta
                        ? `${option.cta} ${option.label}`
                        : option.label
                    }
                    onClick={() => handleOptionClick(option)}
                  >
                    {option.cta}
                  </StartButton>
                </OptionCard>
              ))}
            </OptionGrid>
          </Options>
        ) : null}
      </Card>
    </Section>
  );
};

export default RecommendedCard;
